import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import Header from './Header';
import { mapNamesToWidths } from '../utils/mapNamesToWidths';
import TableBody from './TableBody';
import { getTableMinWidth, makeGetColumnWidth, makeGetFrozenOffsets, setAllWidths } from '../../resizing/redux/widthsSlice';
import { ProvideObserver } from '../../virtualization/context/observerContext';
import { useTableDispatch, useTableSelector } from '../../redux/reduxHooks';
import { setRowRegistry } from '../redux/rowRegistrySlice';
import SelectAllCheckboxHeader from '../../selection/components/SelectAllCheckboxHeader';
import TableTopBar from './TableTopBar';
import { SELECTION_COLUMN_WIDTH, TABLE_PAGINATOR_HEIGHT, TABLE_TOP_BAR_HEIGHT } from '../constants/sizes';
import { Box } from 'foundations-components/transitional/components/Box';
import Paginator from '../../pagination/components/Paginator';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { TableScrollContainer, TableBox, NoFirstDivChildPadding } from './StyledTableComponents';
import { getColumnKey } from '../utils/getColumnKey';
import { useWatchTableContainer } from '../hooks/useWatchTableContainer';
import { getShouldShowBulkActionsContainer } from '../../selection/redux/selectionSlice';
import SelectQueryRow from '../../selection/components/SelectQueryRow';
import { Flex } from 'foundations-components/components/Flex';
import { useShowSelectQuery } from '../hooks/useShowSelectQuery';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Table = ({
  alternateHeaderLeftSlot,
  BulkActionsComponent,
  canSelectAllRecords,
  columns,
  condensed,
  dataDensity,
  CustomSelectAllComponent,
  disableScrollReset,
  getRowId,
  headerLeftSlot,
  headerRightSlot,
  isBulkActionsRedesignEnabled,
  isIndexRedesignEnabled,
  loading,
  noRowsSlot,
  numberOfFrozenColumns,
  onColumnOrderChange,
  onColumnWidthChange,
  onPageIndexChange,
  onPageSizeChange,
  onSortsChange,
  pageable: showPagination,
  pageIndex,
  pageSize,
  pageSizes,
  RowComponent,
  rows,
  scrollContainerRef,
  selectable,
  sorts,
  TableComponent,
  totalRowCount,
  zebraStripe,
  enableFullVirtualization
}) => {
  const rowIds = useMemo(() => rows.map(getRowId), [getRowId, rows]);
  const {
    observer,
    handleTableContainerRef
  } = useWatchTableContainer({
    rowIds,
    disableScrollReset,
    scrollContainerRef
  });
  const handleScrollContainerRef = useCallback(element => {
    handleTableContainerRef(element);
  }, [handleTableContainerRef]);
  const initializedWidths = useRef(false);
  const dispatch = useTableDispatch();
  useLayoutEffect(() => {
    // We only need to set columns widths if the columns changed
    if (!initializedWidths.current) {
      initializedWidths.current = true;
      return;
    }
    dispatch(setAllWidths(mapNamesToWidths(columns)));
  }, [columns, dispatch]);
  useLayoutEffect(() => {
    // Update the row registry when rowIds change
    dispatch(setRowRegistry(rowIds));
  }, [dispatch, rowIds]);
  const sortMap = useMemo(() => sorts.reduce((acc, {
    columnNamespace,
    columnName,
    direction
  }) => {
    acc[getColumnKey(columnNamespace, columnName)] = direction;
    return acc;
  }, {}), [sorts]);
  const tableMinWidth = useTableSelector(getTableMinWidth) + (selectable ? SELECTION_COLUMN_WIDTH : 0);

  // TODO: Add multisort when the index page can handle it
  const handleSortChange = useCallback(({
    columnNamespace,
    columnName,
    direction
  }) => {
    onSortsChange({
      nextSorts: [{
        columnNamespace,
        columnName,
        direction
      }]
    });
  }, [onSortsChange]);
  const {
    showSelectQueryInBulkActionsContainer,
    showSelectQueryRow
  } = useShowSelectQuery(rows, getRowId, canSelectAllRecords, isBulkActionsRedesignEnabled, totalRowCount);
  const shouldShowBulkActionsInTopBar = useTableSelector(getShouldShowBulkActionsContainer) && (!isBulkActionsRedesignEnabled || !isIndexRedesignEnabled);
  const hasHeaderSlots = Boolean(headerLeftSlot || headerRightSlot);
  const showTopBar = shouldShowBulkActionsInTopBar || hasHeaderSlots;
  const getLastColumnWidth = useMemo(() => {
    // HACK: Some consumers will pass an empty columns array. In this case we'll
    // just return 0 since without columns the table doesn't do anything interesting anyway.
    const lastColumn = columns[columns.length - 1];
    if (!lastColumn) {
      return () => 0;
    }
    return makeGetColumnWidth(getColumnKey(lastColumn.columnNamespace, lastColumn.columnName));
  }, [columns]);
  const lastColumnWidth = useTableSelector(getLastColumnWidth);
  const requiredTableProps = useMemo(() => ({
    // HACK: This is required for compat with onboarding tours. Once
    // we have renamed the contact "name" column, we can PR their library
    // to use the actual external id selector and remove this one.
    'data-onboarding': 'grid-table',
    'data-table-external-id': 'framework-data-table',
    'data-test-id': 'framework-data-table',
    'data-test-loading': loading,
    lastColumnWidth,
    minWidth: tableMinWidth
  }), [loading, tableMinWidth, lastColumnWidth]);
  const getFrozenOffsets = useMemo(() => {
    if (numberOfFrozenColumns === 0) {
      return () => [];
    }
    const columnKeys = columns.map(column => {
      return getColumnKey(column.columnNamespace, column.columnName);
    }).slice(0, numberOfFrozenColumns - 1);
    return makeGetFrozenOffsets(columnKeys, selectable);
  }, [columns, numberOfFrozenColumns, selectable]);
  const frozenOffsets = useTableSelector(getFrozenOffsets);
  const thElements = useMemo(() => {
    return columns.map((column, index) => {
      const key = getColumnKey(column.columnNamespace, column.columnName);
      return /*#__PURE__*/_jsx(Header, {
        column: column,
        columnIndex: index,
        columnPosition: frozenOffsets[index],
        isLastColumn: index === columns.length - 1,
        sortDirection: sortMap[key],
        onSortChange: handleSortChange,
        onColumnWidthChange: onColumnWidthChange,
        onColumnOrderChange: onColumnOrderChange,
        numberOfFrozenColumns: numberOfFrozenColumns
      }, key);
    });
  }, [columns, frozenOffsets, handleSortChange, numberOfFrozenColumns, onColumnOrderChange, onColumnWidthChange, sortMap]);
  return /*#__PURE__*/_jsxs(Flex, {
    blockSize: "100%",
    "data-test-id": "framework-data-table-container",
    flexDirection: "column",
    inlineSize: "100%",
    overflow: "hidden",
    children: [showTopBar && /*#__PURE__*/_jsx(Box, {
      shrink: 0,
      basis: TABLE_TOP_BAR_HEIGHT,
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(TableTopBar, {
        alternateHeaderLeftSlot: alternateHeaderLeftSlot,
        BulkActionsComponent: BulkActionsComponent,
        headerLeftSlot: headerLeftSlot,
        headerRightSlot: headerRightSlot,
        isBulkActionsRedesignEnabled: isBulkActionsRedesignEnabled,
        isIndexRedesignEnabled: isIndexRedesignEnabled,
        showSelectQuery: showSelectQueryInBulkActionsContainer,
        totalRowCount: totalRowCount
      })
    }), /*#__PURE__*/_jsxs(TableBox, {
      showPagination: showPagination,
      showTopBar: showTopBar,
      loading: loading,
      children: [loading && /*#__PURE__*/_jsx(NoFirstDivChildPadding, {
        children: /*#__PURE__*/_jsx(LoadingOverlay, {
          "data-test-id": "table-loading",
          contextual: true
        })
      }), rows.length > 0 ? /*#__PURE__*/_jsx(TableScrollContainer, {
        ref: handleScrollContainerRef,
        children: /*#__PURE__*/_jsx(ProvideObserver, {
          value: observer,
          children: /*#__PURE__*/_jsxs(TableComponent, {
            numberOfFrozenColumns: numberOfFrozenColumns,
            frozenOffsets: frozenOffsets,
            requiredTableProps: requiredTableProps,
            zebraStripe: zebraStripe,
            condensed: condensed,
            dataDensity: dataDensity,
            children: [/*#__PURE__*/_jsxs("thead", {
              children: [/*#__PURE__*/_jsxs("tr", {
                children: [selectable && /*#__PURE__*/_jsx(SelectAllCheckboxHeader, {
                  numberOfFrozenColumns: numberOfFrozenColumns,
                  rowIds: rowIds
                }), thElements]
              }), showSelectQueryRow && /*#__PURE__*/_jsx(SelectQueryRow, {
                rowIds: rowIds,
                totalRowCount: totalRowCount,
                columnCount: columns.length + (selectable ? 1 : 0),
                CustomSelectAllComponent: CustomSelectAllComponent
              })]
            }), /*#__PURE__*/_jsx(TableBody, {
              columns: columns,
              getRowId: getRowId,
              numberOfFrozenColumns: numberOfFrozenColumns,
              RowComponent: RowComponent,
              rows: rows,
              selectable: selectable,
              enableFullVirtualization: enableFullVirtualization
            })]
          })
        })
      }) : noRowsSlot]
    }), showPagination && /*#__PURE__*/_jsx(Box, {
      shrink: 0,
      basis: TABLE_PAGINATOR_HEIGHT,
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(Paginator, {
        pageIndex: pageIndex,
        pageSize: pageSize,
        pageSizes: pageSizes,
        totalRowCount: totalRowCount,
        onPageIndexChange: onPageIndexChange,
        onPageSizeChange: onPageSizeChange
      })
    })]
  });
};
export default Table;